import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Newsletter from '../components/Newsletter';
import Footer from '../components/Footer';

interface FAQItem {
  question: string;
  answer: string;
}

interface Review {
  name: string;
  rating: number;
  date: string;
  comment: string;
  avatar: string;
}

const AtelierVelo: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState<number>(0);

  const galleryImages = [
    '/atelier velo/d2d54c06d1016d773aedcb1a4d33fd2e.jpg',
    '/atelier velo/1152eebe1048aa89011d9a5f19fae6dc.jpg',
    '/atelier velo/365b81c600bdae82cba160bb4aee48c6.jpg',
    '/atelier velo/9018602b3fda74385bc7858b94e0bdcf.jpg',
    '/atelier velo/2849a044bbd976b1e57d7f7000e87ae4.jpg'
  ];

  const reviews: Review[] = [
    {
      name: "Thomas R.",
      rating: 5,
      date: "Il y a 2 semaines",
      comment: "Montage impeccable de mon nouveau vélo de route ! L'équipe a pris le temps de tout m'expliquer et de régler le vélo parfaitement à ma morphologie. Un travail de pro.",
      avatar: "TR"
    },
    {
      name: "Sophie M.",
      rating: 5,
      date: "Il y a 1 mois",
      comment: "Révision complète de mon gravel avant une course. Travail soigné, délai respecté et conseils précieux. Je recommande vivement l'atelier Podium !",
      avatar: "SM"
    },
    {
      name: "Marc D.",
      rating: 5,
      date: "Il y a 3 semaines",
      comment: "Réparation urgente de mon dérailleur avant une sortie importante. L'équipe a été très réactive et le travail parfaitement réalisé. Merci !",
      avatar: "MD"
    },
    {
      name: "Claire B.",
      rating: 5,
      date: "Il y a 2 mois",
      comment: "Montage sur mesure de mon vélo de triathlon. Chaque détail a été pensé pour optimiser ma position. Un vrai travail d'artisan passionné.",
      avatar: "CB"
    }
  ];

  const faqItems: FAQItem[] = [
    {
      question: "Puis-je venir sans rendez-vous pour une réparation rapide ?",
      answer: "Nous conseillons de prendre rendez-vous au téléphone ou par mail, mais certains dépannages simples peuvent être acceptés sans rendez-vous selon la disponibilité de l'atelier."
    },
    {
      question: "Faites-vous le montage de pièces achetées à l'extérieur ?",
      answer: "Oui, nous pouvons monter des pièces fournies par le client, sous réserve de compatibilité et après vérification en atelier."
    },
    {
      question: "Réalisez-vous les montages complets à partir d'un cadre nu ?",
      answer: "Oui, nous assurons le montage complet d'un vélo à partir d'un cadre nu : câblerie, cockpit, freins, groupe, roues, composants périphériques."
    },
    {
      question: "Proposez-vous une étude posturale (bikefit) lors d'un montage sur mesure ?",
      answer: "Oui, nous proposons un service d'étude posturale dans notre boutique, inclus dans la formule d'achat d'un vélo complet chez Podium."
    },
    {
      question: "Quels types de freins prenez-vous en charge (disque, patins) ?",
      answer: "Nous intervenons sur les deux : freins à disque (hydrauliques / mécaniques) et freins à patins."
    },
    {
      question: "Pouvez-vous intervenir sur des roues carbone ou haut de gamme ?",
      answer: "Oui, notre atelier est spécialisé dans les composants haut de gamme, y compris roues carbone, moyeux premium et montages spécifiques."
    },
    {
      question: "Quel est le délai moyen pour un montage complet ?",
      answer: "En général entre 48h et 5 jours selon la complexité du montage et la disponibilité des pièces."
    },
    {
      question: "Combien de temps dure une révision complète ?",
      answer: "La durée dépend du diagnostic ; une révision complète standard prend généralement entre 24 et 72 heures selon la charge de l'atelier et la disponibilité des pièces."
    },
    {
      question: "Offrez-vous un réajustement après une révision ?",
      answer: "Oui, un réglage complémentaire peut être proposé gratuitement dans les jours suivant la prestation si nécessaire."
    },
    {
      question: "Puis-je faire entretenir un vélo acheté ailleurs ?",
      answer: "Bien sûr, notre atelier est ouvert à tous les cyclistes, quel que soit le lieu d'achat du vélo."
    },
    {
      question: "Prenez vous en charge les vélos de la marque Canyon ?",
      answer: "Oui nous prenons en charge les vélos Canyon pour révisions et réparation."
    },
    {
      question: "Fournissez-vous du matériel de remplacement en cas d'immobilisation longue durée du vélo ou de certains vos composants en atelier (roues, dérailleurs...) ?",
      answer: "Sur demande et selon disponibilité."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  // Auto-slide carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % galleryImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [galleryImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % galleryImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-black mb-6 leading-tight">
            Atelier vélo haut de gamme à Paris :<br />
            Montage sur mesure, révision & réparation
          </h1>
          
          <p className="text-center text-gray-600 max-w-2xl mx-auto mb-8">
            Notre atelier spécialisé prend en charge vos vélos de route, triathlon et gravel avec le plus grand soin
          </p>

          {/* CTA Button */}
          <div className="text-center mb-12">
            <Link
              to="/devis-atelier"
              className="inline-flex items-center gap-3 bg-black text-white px-8 py-4 rounded-lg hover:bg-gray-800 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Demander un devis
            </Link>
          </div>
          
          {/* Main Image */}
          <div className="relative rounded-lg overflow-hidden mb-12 max-w-4xl mx-auto">
            <img
              src="/atelier-podium.png"
              alt="Atelier vélo professionnel"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </section>

      {/* Content Section with Sidebar */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-black mb-4">
                  Révision & Réparation de vélo de route, triathlon et gravel à Paris
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Nous réalisons des <strong>montages sur mesure</strong>, des <strong>révisions complètes</strong> et des <strong>réparations techniques</strong> pour <strong>vélos de route, de triathlon et de gravel</strong>.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Nous proposons également des services de <Link to="/lavage-velo" className="text-black font-semibold underline hover:no-underline">lavage de vélo</Link>.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-black mb-4">Nos services atelier</h2>
                
                <h3 className="font-semibold text-black mt-6 mb-2">🛠️ Montage sur mesure (Route / Triathlon / Gravel)</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Assemblage de vélos sur mesure (à partir d'un cadre, montage du groupe, des roues, des composants périphérique, et possibilité de réglages ultra précis avec notre service d'<Link to="/etude-posturale" className="text-black font-semibold underline hover:no-underline">étude posturale</Link>.
                </p>

                <h3 className="font-semibold text-black mt-6 mb-2">🔧 Révision complète & entretien</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Révision standard et révision complète (démontage, nettoyage, contrôle usure, remontage, lubrification, réglages freins et transmission). Idéal avant une épreuve, en début ou en fin de saison.
                </p>

                <h3 className="font-semibold text-black mt-6 mb-2">⚡ Réparation & dépannage technique</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Diagnostique précis des composants d'usures, d'éventuels problèmes mécaniques ou pannes électroniques (groupes mécaniques ou groupes électronique) : transmission, freins, direction, roues, etc. Réparations et remplacement de pièces si nécessaire.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-black mb-2">⚠️ Spécificités & exclusions</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Nous prenons en charge uniquement les vélos de <strong>route</strong>, <strong>triathlon</strong> et <strong>gravel</strong>. <strong>Ne sont pas acceptés :</strong> vélos de ville, électriques (VAE) et trottinettes.
                </p>
                <p className="text-gray-600 text-sm mb-4">
                  Si nous décidons au moment de faire la révision/réparation du vélo qu'un changement de composants est nécessaire, nous vous contacterons avant d'effectuer les travaux pour valider les changements avec vous et vous communiquer les prix.
                </p>
                <p className="text-gray-600 text-sm">
                  Pour que nous puissions intervenir efficacement sur le vélo, celui-ci doit être propre. Si vous n'avez pas la possibilité de nous confier votre vélo suffisamment propre, un de nos <Link to="/lavage-velo" className="text-black font-semibold underline hover:no-underline">forfaits de lavage</Link> vous sera proposé et facturé.
                </p>
              </div>

              {/* Second CTA */}
              <div className="pt-4">
                <Link
                  to="/devis-atelier"
                  className="inline-flex items-center gap-2 bg-gray-100 text-black px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Faire une demande de devis
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-lg p-6 sticky top-24">
                <h3 className="text-xl font-bold text-black mb-6">Comment ça se passe ?</h3>
                <ul className="space-y-4 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="mr-3 text-lg">📞</span>
                    <span>Contactez-nous pour expliquer la prestation désirée et pour convenir d'un rendez-vous.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 text-lg">📍</span>
                    <span>Déposez votre vélo dans notre boutique : 26 Bd Henri Sellier, 92150 Suresnes</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 text-lg">📝</span>
                    <span>Évaluation technique et devis avant toute intervention.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 text-lg">🔧</span>
                    <span>Intervention en atelier par notre équipe de techniciens spécialisés.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 text-lg">✅</span>
                    <span>Contrôle final et restitution avec conseils d'entretien.</span>
                  </li>
                </ul>

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h4 className="font-bold text-black mb-4">Nous contacter</h4>
                  <div className="space-y-2 text-sm">
                    <p className="flex items-center gap-2">
                      <span>📧</span>
                      <a href="mailto:contact@podium.fr" className="text-black hover:underline">contact@podium.fr</a>
                    </p>
                    <p className="flex items-center gap-2">
                      <span>📱</span>
                      <a href="tel:+33123456789" className="text-black hover:underline">01 23 45 67 89</a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery Carousel */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center text-black mb-8">
            Notre atelier en images
          </h2>
          
          {/* Carousel */}
          <div className="relative max-w-4xl mx-auto mb-12">
            <div className="relative h-[300px] md:h-[400px] lg:h-[500px] rounded-2xl overflow-hidden shadow-xl">
              {galleryImages.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-500 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={image}
                    alt={`Atelier Podium ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
              
              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              
              {/* Dots */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {galleryImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      index === currentSlide ? 'bg-white w-6' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`aspect-square rounded-lg overflow-hidden cursor-pointer transition-all ${
                  index === currentSlide ? 'ring-4 ring-black scale-105' : 'opacity-70 hover:opacity-100'
                }`}
              >
                <img
                  src={image}
                  alt={`Atelier Podium ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center text-black mb-4">
            Ils ont fait confiance à Podium pour leur montage sur mesure ou révision vélo
          </h2>
          <div className="flex justify-center items-center gap-2 mb-8">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg key={star} className="w-6 h-6 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <span className="text-gray-600 ml-2">4.9/5 basé sur les avis Google</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reviews.map((review, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-gray-700 to-gray-900 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {review.avatar}
                  </div>
                  <div>
                    <p className="font-semibold text-black">{review.name}</p>
                    <p className="text-xs text-gray-500">{review.date}</p>
                  </div>
                </div>
                <div className="flex mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className={`w-4 h-4 ${star <= review.rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">"{review.comment}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-black mb-12">Questions fréquentes</h2>
          
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full text-left flex justify-between items-center p-4 hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-black pr-4">{item.question}</span>
                  <svg
                    className={`w-5 h-5 flex-shrink-0 transform transition-transform ${openFAQ === index ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFAQ === index && (
                  <div className="px-4 pb-4">
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-black mb-4">
            Prêt à confier votre vélo à nos experts ?
          </h2>
          <p className="text-gray-600 mb-8">
            Demandez un devis gratuit pour votre réparation ou montage sur mesure
          </p>
          <Link
            to="/devis-atelier"
            className="inline-flex items-center gap-3 bg-black text-white px-10 py-4 rounded-lg hover:bg-gray-800 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Demander un devis gratuit
          </Link>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </div>
  );
};

export default AtelierVelo;
